import React, { useState } from "react";
import {
    Box,
    Chip,
    Collapse,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BORDA_SUAVE, NAVY, SOMBRA_CARD, SUPERFICIE, rotuloStatus } from "./utils";

const campoSx = {
    minWidth: "8.5rem",
    "& .MuiOutlinedInput-root": {
        backgroundColor: "white",
        borderRadius: "10px",
        "& fieldset": { borderColor: "#DCE3EF" },
        "&:hover fieldset": { borderColor: "#B7C3D9" },
        "&.Mui-focused fieldset": { borderColor: NAVY }
    }
};

const chipSx = (ativo) => (ativo
    ? {
        backgroundColor: NAVY,
        color: "white",
        fontWeight: 600,
        "&:hover": { backgroundColor: NAVY }
    }
    : {
        backgroundColor: "white",
        color: "#5A6B8C",
        border: "1px solid #DCE3EF",
        "&:hover": { backgroundColor: "rgba(19, 35, 80, 0.06)" }
    });

const OPCOES_STATUS = [
    { valor: "", rotulo: "Todos" },
    { valor: "G", rotulo: rotuloStatus("G") },
    { valor: "C", rotulo: rotuloStatus("C") },
    { valor: "I", rotulo: rotuloStatus("I") },
    { valor: "X", rotulo: rotuloStatus("X") },
    { valor: "nao-gerado", rotulo: "Não gerado" }
];

const FiltrosIntegracao = ({ filtros, onChange, diasFaturamento = [], diasVencimento = [], aba }) => {
    const [expandido, setExpandido] = useState(false);

    function alterar(campo, valor) {
        onChange({ ...filtros, [campo]: valor });
    }

    function limparFiltros(ev) {
        ev.stopPropagation();
        onChange(Object.fromEntries(Object.keys(filtros).map((chave) => [chave, ""])));
    }

    const qtdFiltrosAtivos = Object.values(filtros).filter((valor) => valor !== "").length;
    const temFiltrosAtivos = qtdFiltrosAtivos > 0;

    return (
        <Box sx={{ mb: 3 }}>
            <Box
                role="button"
                tabIndex={0}
                aria-expanded={expandido}
                onClick={() => setExpandido((atual) => !atual)}
                onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        setExpandido((atual) => !atual);
                    }
                }}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    px: 1.5,
                    py: 0.75,
                    cursor: "pointer",
                    userSelect: "none",
                    borderRadius: "10px",
                    transition: "background-color 0.15s ease",
                    "&:hover": { backgroundColor: "rgba(19, 35, 80, 0.06)" }
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <FilterAltOutlinedIcon sx={{ fontSize: "1.15rem", color: NAVY }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                        Filtrar
                    </Typography>
                    {temFiltrosAtivos && (
                        <Chip
                            label={qtdFiltrosAtivos}
                            size="small"
                            sx={{
                                height: "1.25rem",
                                minWidth: "1.25rem",
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                backgroundColor: NAVY,
                                color: "white",
                                "& .MuiChip-label": { px: "0.4rem" }
                            }}
                        />
                    )}
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                    {temFiltrosAtivos && (
                        <Typography
                            variant="caption"
                            onClick={limparFiltros}
                            sx={{
                                fontWeight: 600,
                                color: "#5A6B8C",
                                cursor: "pointer",
                                "&:hover": { color: NAVY, textDecoration: "underline" }
                            }}
                        >
                            Limpar
                        </Typography>
                    )}
                    <ExpandMoreIcon
                        aria-hidden="true"
                        sx={{
                            color: "#5A6B8C",
                            fontSize: "1.25rem",
                            transition: "transform 0.2s ease",
                            transform: expandido ? "rotate(180deg)" : "rotate(0deg)"
                        }}
                    />
                </Stack>
            </Box>

            <Collapse in={expandido} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: 1,
                        p: 2,
                        borderRadius: "16px",
                        backgroundColor: SUPERFICIE,
                        border: `1px solid ${BORDA_SUAVE}`,
                        boxShadow: SOMBRA_CARD
                    }}
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
                                Dia Faturamento
                            </Typography>
                            <TextField
                                select
                                size="small"
                                value={filtros.diaFaturamento}
                                onChange={(e) => alterar("diaFaturamento", e.target.value)}
                                sx={campoSx}
                                inputProps={{ "aria-label": "Dia Faturamento" }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {diasFaturamento.map((dia) => (
                                    <MenuItem key={dia} value={dia}>{String(dia).padStart(2, "0")}</MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
                                Dia Vencimento
                            </Typography>
                            <TextField
                                select
                                size="small"
                                value={filtros.diaVencimento}
                                onChange={(e) => alterar("diaVencimento", e.target.value)}
                                sx={campoSx}
                                inputProps={{ "aria-label": "Dia Vencimento" }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {diasVencimento.map((dia) => (
                                    <MenuItem key={dia} value={dia}>{String(dia).padStart(2, "0")}</MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
                                Número de Série
                            </Typography>
                            <TextField
                                size="small"
                                placeholder="Ex: 000000"
                                value={filtros.numeroSerie}
                                onChange={(e) => alterar("numeroSerie", e.target.value)}
                                sx={campoSx}
                            />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: "14rem", display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
                                {aba === "bma" ? "Cliente" : "Revenda"}
                            </Typography>
                            <TextField
                                size="small"
                                placeholder={aba === "bma" ? "Buscar cliente..." : "Buscar revenda..."}
                                value={filtros.busca}
                                onChange={(e) => alterar("busca", e.target.value)}
                                sx={{ ...campoSx, minWidth: 0 }}
                                fullWidth
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
                            Status
                        </Typography>
                        <Stack direction="row" spacing={0} useFlexGap flexWrap="wrap" sx={{ gap: 0.75 }}>
                            {OPCOES_STATUS.map((opcao) => (
                                <Chip
                                    key={String(opcao.valor)}
                                    label={opcao.rotulo}
                                    size="small"
                                    clickable
                                    onClick={() => alterar("status", opcao.valor)}
                                    sx={chipSx(filtros.status === opcao.valor)}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default React.memo(FiltrosIntegracao);
